import { TooltipOrToast } from "@faire/retailer-visitor-shared/components/TooltipOrToast/TooltipOrToast";
import { useIdvStatus } from "@faire/retailer-visitor-shared/hooks/idv/useIdvStatus";
import { useRetailer } from "@faire/retailer-visitor-shared/hooks/retailer/useRetailer";
import {
  getUnverifiedRetailerTooltipLabels,
  getIdvTransparencyMessage,
  useGetCanMessageBrand,
  useShouldShowVerificationTooltip,
} from "@faire/retailer-visitor-shared/lib/messageBrandCTAUtils";
import { useUserStore } from "@faire/retailer-visitor-shared/stores/domain/useUserStore";
import { LazyContactBrandModal } from "@faire/retailer-visitor-shared/views/brand/ContactBrand/modal/index";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { trackEventOnClick } from "@faire/web--source/common/trackEvent";
import { Button } from "@faire/web--source/slate/Button";
import { ChatBubble } from "@faire/web--source/slate/icons/foundation/ChatBubble";
import { Information } from "@faire/web--source/slate/icons/foundation/Information";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Tooltip } from "@faire/web--source/slate/Tooltip";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackRetailerPortalProductPageMessageBrandClick } from "@faire/web-api--source/events/retailerPortal/click/productPageMessageBrand";
import { IProductPageResponse } from "@faire/web-api--source/indigofair/data/IProductPageResponse";
import React, { Suspense, useState } from "react";

interface IProps {
  productName?: string;
  brand: Pick<
    IProductPageResponse.IPdpBrand,
    | "token"
    | "hide_chat"
    | "active"
    | "name"
    | "owner_first_name"
    | "upper_bound_lead_time_days"
  >;
  isInFooter?: boolean;
  showLargeButton?: boolean;
}

export const MessageBrandCTA: React.FC<IProps> = (props) => {
  const { strictLocalize } = useStrictLocalization();
  const { retailer } = useRetailer();
  const { hasPendingIdv } = useIdvStatus();
  const pendingIdv = hasPendingIdv === "yes";
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const { brand, productName, showLargeButton } = props;

  const getCanMessageBrand = useGetCanMessageBrand();
  const canMessageBrand = getCanMessageBrand(brand);
  const shouldShowVerificationTooltip = useShouldShowVerificationTooltip(
    brand,
    retailer?.identity_confirmed ?? false
  );

  const toggleModal = () => {
    if (!isModalOpen) {
      trackRetailerPortalProductPageMessageBrandClick("true");
    }
    setIsModalOpen(!isModalOpen);
  };

  const button = showLargeButton ? (
    <Button
      variant="secondary"
      onClick={toggleModal}
      disabled={!canMessageBrand}
    >
      <ChatBubble color="inherit" />
      <Spacer size="1x" />
      <StrictLocalMsg
        defaultMessage="Message Brand"
        description={{
          text: "Click this button to send a message to the brand.",
        }}
      />
    </Button>
  ) : (
    <Button
      variant="tertiary"
      onClick={toggleModal}
      disabled={!canMessageBrand}
    >
      <StrictLocalMsg
        defaultMessage="Message the Brand"
        description={{ text: "button to send an email to the brand" }}
      />
    </Button>
  );

  const modal = isModalOpen ? (
    <Suspense fallback={null}>
      <LazyContactBrandModal
        productName={productName}
        brand={brand}
        closeContactMakerModal={toggleModal}
      />
    </Suspense>
  ) : null;

  const { isRetailerSubUser } = useUserStore(["isRetailerSubUser"]);

  const { messageLabel, action } = getIdvTransparencyMessage(
    strictLocalize,
    pendingIdv,
    isRetailerSubUser
  );

  if (shouldShowVerificationTooltip) {
    return (
      <>
        <TooltipOrToast
          position={props.isInFooter ? "top" : "bottom"}
          pointerOffset={props.isInFooter ? -50 : 0}
          primary
          targetNode={button}
          contents={[
            {
              body: messageLabel,
            },
          ]}
          action={
            action == null ? null : (
              <Button
                variant="tertiary"
                tertiaryColor="inherit"
                linkTo={action.link}
                onClick={trackEventOnClick(
                  "brand-messenger-tooltip/idv_request/link"
                )}
                target="_blank"
              >
                {action.label}
              </Button>
            )
          }
        />
      </>
    );
  } else {
    const unverifiedTooltipLabels =
      getUnverifiedRetailerTooltipLabels(strictLocalize);
    return (
      <>
        {button}
        {shouldShowVerificationTooltip ? (
          <>
            <Spacer size="1x" />
            <Tooltip
              primary
              position="bottom"
              icon={<Information width="12px" height="12px" />}
              contents={[
                {
                  title: unverifiedTooltipLabels.title,
                  body: unverifiedTooltipLabels.message,
                },
              ]}
            />
          </>
        ) : null}
        {modal}
      </>
    );
  }
};
